/** ms since epoch; supports numeric strings and unix seconds. */
function coerceEpochMs(value: unknown): number {
  if (value == null || value === "") return Number.NaN;
  if (typeof value === "boolean") return Number.NaN;
  const n = typeof value === "number" ? value : Number(String(value).trim());
  if (!Number.isFinite(n)) return Number.NaN;
  if (n > 1e12) return n;
  if (n > 1e9) return n * 1000;
  return Number.NaN;
}

/**
 * Prefer wire `createdAtMs` (JSON often delivers it as a string; Number.isFinite alone misses that).
 * Then parse ISO / MySQL-style strings without treating "T... without Z" as local (avoids ~TZ offset bugs).
 */
export function resolveInstantMs(
  iso: string | null | undefined,
  epochMs?: unknown,
): number {
  const fromMs = coerceEpochMs(epochMs);
  if (!Number.isNaN(fromMs)) return fromMs;

  if (iso == null) return Number.NaN;
  const raw = String(iso).trim().replace(/\r\n?/g, "");
  if (!raw) return Number.NaN;

  const hasExplicitZone =
    /[zZ]$/.test(raw) ||
    /[+\-]\d{1,2}:\d{2}$/.test(raw) ||
    /[+\-]\d{2}:\d{2}:\d{2}$/.test(raw);

  if (hasExplicitZone) {
    const ts = Date.parse(raw);
    return Number.isNaN(ts) ? Number.NaN : ts;
  }

  if (/^\d{4}-\d{2}-\d{2}T\d/.test(raw)) {
    const ts = Date.parse(`${raw}Z`);
    return Number.isNaN(ts) ? Number.NaN : ts;
  }

  if (/^\d{4}-\d{2}-\d{2} \d{1,2}:\d{2}/.test(raw)) {
    const ts = Date.parse(raw.replace(" ", "T") + "Z");
    return Number.isNaN(ts) ? Number.NaN : ts;
  }

  const ts = Date.parse(raw);
  return Number.isNaN(ts) ? Number.NaN : ts;
}

/** Short relative time for dashboard copy */
export function parseApiDateToEpoch(
  input: string | number | null | undefined,
): number {
  if (input == null) return Number.NaN;

  const fromNum = coerceEpochMs(input);
  if (!Number.isNaN(fromNum)) return fromNum;

  return resolveInstantMs(String(input), undefined);
}

export function formatRelativeTime(
  iso: string | null | undefined,
  epochMs?: unknown,
): string {
  const t = resolveInstantMs(iso, epochMs);
  if (Number.isNaN(t)) return "";

  const diffMs = Date.now() - t;
  const absMins = Math.floor(Math.abs(diffMs) / 60000);
  if (absMins < 1) return "just now";

  if (absMins < 60) return `${absMins}m ago`;

  const hrs = Math.floor(absMins / 60);
  if (hrs < 24) return `${hrs}h ago`;

  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;

  return new Date(t).toLocaleDateString();
}

export function formatUpcomingSession(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (isToday) return `Today, ${time}`;
  if (isTomorrow) return `Tomorrow, ${time}`;
  return `${d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}, ${time}`;
}
